import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { useGetLoanApplication, useUploadDocument, useGetDocumentsByLoanApplication, useGetDocumentsByCustomer, useReuseDocument, useDeleteDocument, useSubmitLoanDocuments, cancelLoanApplication } from '../../../services/loanService';
import { downloadDocument } from '../../../services/documentService';
import { useGetCustomer, useUpdateKycTimestamp } from '../../../services/customerService';
import { useIndividualKyc, confirmIndividualKyc, individualKycStatus } from '../../../services/individualKycService';
import { UploadDocumentBodyDocumentType } from '../../../services/schemas/loanServiceSchemas/uploadDocumentBodyDocumentType';
import type { LoanApplication } from '../../../services/schemas/loanServiceSchemas/loanApplication';
import type { Individual as CustomerIndividual } from '../../../services/schemas/customerServiceSchemas/individual';
import type { DocumentReference } from '../../../services/schemas/loanServiceSchemas/documentReference';
import type { LoanDetails } from '../../../services/schemas/loanServiceSchemas/loanDetails';
import LoanApplicationComments from '../../../components/common/LoanApplicationComments';
import './LoanApplicationDocuments.css';
import StatementIqPanel from '../../loan-application/steps/StatementIqPanel';
import DocumentActionsMenu from '../../common/DocumentActionsMenu';
import { DocumentPreviewModal, useDocumentPreview } from '../../common/DocumentPreview';
import { DOCUMENT_VALIDITY_MONTHS, isDocumentExpired } from '../../../utils/documentExpiry';
import { getFundingProduct } from '../../../constants/fundingProducts';
import { formatDocumentDate, formatDocumentPeriod, getDocumentUploadDate, getDocumentUploader } from '../../../utils/documentDates';

interface ExpectedDocument {
  type: string;
  label: string;
  description: string;
  required: boolean;
  /** Several files side by side (monthly bank statements, quotations) rather than one replaced. */
  multiple?: boolean;
  /** Each file is for a period, e.g. the month a bank statement covers. */
  withPeriod?: boolean;
  /** The person the document is about, for documents kept per director. */
  directorId?: string;
  directorName?: string;
}

/** Types the API keeps several of per application; see DocumentType.multiple on the API. */
const MULTI_FILE_TYPES = new Set([
  'BANK_STATEMENT',
  'OTHER_CORPORATE_DOCUMENT',
  'SUPPLIER_QUOTATION',
  'PAYMENT_CERTIFICATES',
  'DIRECTOR_OTHER_DOCUMENT'
]);

/**
 * Documents held for each director (guideline section B), so there is no single shared
 * "representative ID" slot standing in for every director.
 */
const DIRECTOR_DOCUMENTS: ExpectedDocument[] = [
  { type: 'DIRECTOR_ID_COPY', label: 'ID / Passport', description: 'Valid until it expires, unless the details change.', required: true },
  { type: 'DIRECTOR_PROOF_OF_ADDRESS', label: 'Proof of Address', description: 'Not older than 3 months.', required: false },
  // No Credit Bureau Report upload: the bureau check is run from the LMS (finding #17)
  { type: 'DIRECTOR_CONSENT', label: 'Director Consent', description: 'Signed consent for credit and background checks.', required: false },
  { type: 'DIRECTOR_OTHER_DOCUMENT', label: 'Other Supporting Documents', description: 'Anything else supplied for this director.', required: false, multiple: true }
];

interface Director {
  /** The director's id in the customer service; documents are stored against it. */
  key: string;
  name: string;
  idNumber?: string;
}

// Keep individual document wording independent of the company checklist.
const INDIVIDUAL_DOCUMENTS: ExpectedDocument[] = [
  { type: 'ID_DOCUMENT', label: 'ID Document / Passport', description: 'Copy of the applicant’s RSA ID or passport. Use an existing current copy on file where available.', required: true },
  { type: 'PROOF_OF_ADDRESS', label: 'Proof of Address', description: 'Utility bill or lease agreement, not older than 3 months.', required: true },
  { type: 'BANK_LETTER', label: 'Bank Confirmation Letter', description: "Confirmation of the applicant's bank account details, not older than 3 months.", required: true },
  { type: 'PAYSLIP', label: 'Latest Payslip', description: 'Most recent payslip. Required for permanent employment at affordability review. Enter the date printed on the payslip before uploading.', required: false },
  { type: 'BANK_STATEMENT', label: 'Bank Statements', description: 'Most recent 6 months, with the latest statement not older than 30 days. Add each month as it comes.', required: false, multiple: true, withPeriod: true }
];

/**
 * The company's own documents, grouped as the business's document guideline lays them out
 * (section A). Descriptions carry the guideline's validity rule so the officer knows how recent a
 * copy has to be while collecting it.
 */
const COMPANY_DOCUMENTS: ExpectedDocument[] = [
  {
    type: 'COMPANY_REGISTRATION_CERTIFICATE',
    label: 'Company Registration Certificate',
    description: 'CIPC registration documents. Refresh every 12 months, or when company or director details change.',
    required: true
  },
  {
    type: 'PROOF_OF_ADDRESS',
    label: 'Company Proof of Address',
    description: 'Utility bill or lease agreement, not older than 3 months.',
    required: true
  },
  {
    type: 'BANK_LETTER',
    label: 'Bank Confirmation Letter',
    description: "Confirmation of the company's bank account details, not older than 3 months.",
    required: true
  },
  {
    type: 'TAX_CLEARANCE',
    label: 'Tax Compliance Status / TCS PIN',
    description: 'SARS tax compliance status or TCS PIN. Verify at every new application.',
    required: true
  },
  {
    type: 'FINANCIAL_STATEMENTS',
    label: 'Financial Statements',
    description: 'Latest completed financial year.',
    required: false
  },
  {
    type: 'MANAGEMENT_ACCOUNTS',
    label: 'Management Accounts',
    description: 'Preferably not older than 3 months.',
    required: false
  },
  {
    type: 'BANK_STATEMENT',
    label: 'Company Bank Statements',
    description: 'Most recent 6 months, with the latest statement not older than 30 days. Add each month as it comes.',
    required: false,
    multiple: true,
    withPeriod: true
  },
  {
    type: 'OTHER_CORPORATE_DOCUMENT',
    label: 'Other Corporate Documents',
    description: 'Any other company document the application relies on.',
    required: false,
    multiple: true
  }
];

const REPRESENTATIVE_DOCUMENTS: ExpectedDocument[] = [
  {
    type: 'COMPANY_REP_ID_COPY',
    label: 'Company Representative ID',
    description: 'ID copy of the company representative if not a director.',
    required: true
  },
  {
    type: 'DELEGATION_OF_AUTHORITY',
    label: 'Delegation of Authority',
    description: 'Delegation of authority if the representative is not a director.',
    required: false
  }
];

/** Offered when the application has no funding product with its own document list. */
const DEFAULT_TRANSACTION_DOCUMENTS: ExpectedDocument[] = [
  {
    type: 'PROOF_OF_CONTRACT',
    label: 'Proof of Contract',
    description: 'Proof of contract if funding is for a project.',
    required: false
  }
];

const COMPANY_DOCUMENT_TYPES = new Set(COMPANY_DOCUMENTS.map(doc => doc.type));

/**
 * The transaction documents the funding product chosen at origination asks for. Some products
 * also lean on company documents (working capital needs management accounts, bridge needs bank
 * evidence); those are collected once, in the company section, rather than as a second slot of
 * the same type here -- one slot per type, since an upload replaces the previous one of its type.
 */
const getTransactionDocuments = (fundingType?: string): ExpectedDocument[] => {
  const product = getFundingProduct(fundingType);
  if (!product) return DEFAULT_TRANSACTION_DOCUMENTS;

  const seen = new Set<string>();
  return product.documents
    .filter(doc => !COMPANY_DOCUMENT_TYPES.has(doc.type))
    .filter(doc => (seen.has(doc.type) ? false : (seen.add(doc.type), true)))
    .map(doc => ({
      type: doc.type,
      label: doc.label,
      description: doc.required ? `Required for ${product.shortName}.` : 'Where applicable.',
      required: doc.required,
      multiple: MULTI_FILE_TYPES.has(doc.type)
    }));
};

/** Company document types the funding product requires, over and above the company's own rules. */
const getProductRequiredCompanyTypes = (fundingType?: string): Set<string> =>
  new Set(
    (getFundingProduct(fundingType)?.documents || [])
      .filter(doc => doc.required && COMPANY_DOCUMENT_TYPES.has(doc.type))
      .map(doc => doc.type)
  );

interface DisplayDocument {
  id: string;
  documentType: string;
  documentKey: string;
  name: string;
  size: string;
  /** When the file first entered the LMS -- not when it was attached to this application. */
  uploadDate?: string;
  uploadedBy?: string;
  directorId?: string;
  directorName?: string;
  period?: string;
  documentDate?: string;
  expiryDate?: string;
  supersededDate?: string;
}

const sameSubject = (a?: string | null, b?: string | null) => (a || '') === (b || '');

const expectedKey = (doc: ExpectedDocument) => `${doc.type}:${doc.directorId || ''}`;

const IndividualLoanApplicationDocuments: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [payslipDate, setPayslipDate] = useState('');
  const [pendingDates, setPendingDates] = useState<Record<string, { documentDate?: string; expiryDate?: string }>>({});
  useEffect(() => { setPayslipDate(''); setPendingDates({}); }, [id]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [downloadingDocuments, setDownloadingDocuments] = useState<Set<string>>(new Set());
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showExpiredDocsModal, setShowExpiredDocsModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const navigate = useNavigate();

  // Add loading overlay state
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  // KYC related state
  const [isUpdatingKyc, setIsUpdatingKyc] = useState(false);
  const [kycError, setKycError] = useState<string | null>(null);

  // Fetch loan application details directly using loan application ID
  const { data: loanApplication, isLoading: isLoadingLoan, refetch: refetchLoanApplication } = useGetLoanApplication(id!, {});

  const isIndividual = loanApplication?.loanType === 'PERSONAL';
  const fundingType = loanApplication?.loanDetails?.fundingType;
  const fundingProduct = getFundingProduct(fundingType);
  const productRequiredCompanyTypes = getProductRequiredCompanyTypes(fundingType);
  const companyDocuments: ExpectedDocument[] = COMPANY_DOCUMENTS.map(doc =>
    fundingProduct && productRequiredCompanyTypes.has(doc.type)
      ? { ...doc, required: true, description: `${doc.description} Required for ${fundingProduct.shortName}.` }
      : doc
  );

  /**
   * The documents this application is expected to carry, and which of them the business treats as
   * required. Nothing here blocks submission: verification, credit and collateral work run
   * alongside document collection, so holding the whole file back for one outstanding page stalls
   * work that could already be happening. What is missing is recorded and shown instead.
   */
  /**
   * Directors come from the customer's company profile. Each is keyed by their id in the customer
   * service, which is stable across applications, so their documents can be reused next time.
   */
  const directors: Director[] = Array.from(loanApplication?.company?.directors || [])
    .filter(director => !!director.id)
    .map(director => ({
      key: director.id as string,
      name: `${director.firstName || ''} ${director.lastName || ''}`.trim() || 'Unnamed director',
      idNumber: director.idNumber
    }));

  const directorDocuments = (director: Director): ExpectedDocument[] =>
    DIRECTOR_DOCUMENTS.map(doc => ({ ...doc, directorId: director.key, directorName: director.name }));

  // Fetch documents using the loan application ID
  // Every version, so replaced documents can be shown as history; the screen works on the current ones.
  const { data: allDocuments, isLoading: isLoadingDocuments, refetch: refetchDocuments } = useGetDocumentsByLoanApplication(
    id || '',
    { includeSuperseded: true },
    { query: { enabled: !!id } }
  );
  const documents = (allDocuments || []).filter(doc => doc.status !== 'SUPERSEDED');
  const previousVersions = (allDocuments || []).filter(doc => doc.status === 'SUPERSEDED');

  // Upload document mutation
  const uploadDocumentMutation = useUploadDocument();

  // Submit documents mutation
  const submitDocumentsMutation = useSubmitLoanDocuments();

  // Preserve company lookup; individual customers are found by their captured ID.
  const reuseDocumentMutation = useReuseDocument();
  const deleteDocumentMutation = useDeleteDocument();
  const [removingDocumentId, setRemovingDocumentId] = useState<string | null>(null);
  const documentPreview = useDocumentPreview(message => toast.error(message));

  const handleRemoveDocument = async (documentId: string, documentName: string) => {
    setRemovingDocumentId(documentId);
    try {
      await deleteDocumentMutation.mutateAsync({ documentId });
      await refetchDocuments();
      toast.success(`${documentName} removed`);
    } catch (error) {
      console.error('Error removing document:', error);
      toast.error('Could not remove the document');
    } finally {
      setRemovingDocumentId(null);
    }
  };
  const [reusingDocumentId, setReusingDocumentId] = useState<string | null>(null);

  // Get customer information using company registration number
  const { data: partyData, isLoading: isLoadingParty } = useGetCustomer(
    isIndividual ? (loanApplication?.customerPartyId ? {
      customerId: loanApplication.customerPartyId
    } : loanApplication?.individual?.idNumber ? {
      idNumber: loanApplication.individual.idNumber
    } : undefined) : loanApplication?.company?.companyRegNumber ? {
      registrationNumber: loanApplication.company.companyRegNumber
    } : undefined,
    {
      query: {
        enabled: isIndividual ? !!(loanApplication?.customerPartyId || loanApplication?.individual?.idNumber) : !!loanApplication?.company?.companyRegNumber
      }
    }
  );

  const individualKyc = useIndividualKyc(isIndividual ? partyData?.id : undefined);
  // Older application snapshots can omit employment; use the explicit customer profile already loaded here.
  const employmentType = loanApplication?.individual?.employmentType
    || (partyData?.info?.type === 'INDIVIDUAL' ? (partyData.info as CustomerIndividual).employmentType : undefined);
  const applicationPartyDocuments: ExpectedDocument[] = isIndividual
    ? [...INDIVIDUAL_DOCUMENTS, ...(employmentType === 'CONTRACT' ? [{
      type: 'EMPLOYMENT_CONTRACT', label: 'Employment Contract / Appointment Letter',
      description: 'Contract or appointment evidence for contract employment. Required at affordability review.',
      required: false
    }] : [])]
    : companyDocuments;
  // The generic project-contract fallback belongs to the company flow; configured product documents stay intact.
  const transactionDocuments = getTransactionDocuments(fundingType).filter(doc =>
    !isIndividual || doc.type !== 'PROOF_OF_CONTRACT' || !!fundingProduct
  );
  const expectedDocuments: ExpectedDocument[] = [
    ...applicationPartyDocuments,
    ...(isIndividual ? [] : directors.flatMap(directorDocuments)),
    ...(isIndividual ? [] : REPRESENTATIVE_DOCUMENTS),
    ...transactionDocuments
  ];
  // Documents this customer has already supplied on earlier applications, so a client is not
  // asked twice for the same paper.
  const { data: customerDocuments, refetch: refetchCustomerDocuments } = useGetDocumentsByCustomer(
    partyData?.id || '',
    { query: { enabled: !!partyData?.id } }
  );

  // KYC update mutation
  const updateKycMutation = useUpdateKycTimestamp();

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    options: { directorId?: string; directorName?: string; period?: string; documentDate?: string; expiryDate?: string } = {}
  ) => {
    if (!e.target.files || e.target.files.length === 0) return;
    if (documentType === 'PAYSLIP' && !options.documentDate) {
      toast.error('Enter the payslip date before uploading');
      return;
    }

    try {
      setIsUploading(true);
      setShowLoadingOverlay(true);
      setUploadError(null);
      const file = e.target.files[0];

      if (file.size > 100 * 1024 * 1024) {
        toast.error(`File size exceeds 100MB limit: ${file.name}`);
        return;
      }

      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}. Please upload PDF, JPG, or PNG files.`);
        return;
      }

      // Generate a unique filename by appending milliseconds
      const timestamp = new Date().getTime().toString().slice(-4);
      const fileExtension = file.name.split('.').pop();
      const baseName = file.name.substring(0, file.name.lastIndexOf('.'));
      const uniqueFileName = `${baseName}_${timestamp}.${fileExtension}`;

      // Create a new File object with the unique name
      const uniqueFile = new File([file], uniqueFileName, { type: file.type });

      // Upload the document with the unique filename
      await uploadDocumentMutation.mutateAsync({
        loanApplicationId: id || '',
        data: {
          file: uniqueFile,
          documentType: documentType as UploadDocumentBodyDocumentType,
          description: options.directorName
            ? `${documentType.toLowerCase().replace(/_/g, ' ')} for ${options.directorName}`
            : `Uploaded ${documentType.toLowerCase().replace(/_/g, ' ')}`,
          // Scopes replacement to this director, so the second director's ID copy does not
          // replace the first one's.
          directorId: options.directorId,
          directorName: options.directorName,
          period: options.period,
          documentDate: options.documentDate,
          expiryDate: options.expiryDate
        }
      });

      // Wait for the documents to be refetched
      await refetchDocuments();

      toast.success('File uploaded successfully');
      setPendingDates(prev => ({ ...prev, [`${documentType}:${options.directorId || ''}`]: {} }));

    } catch (error) {
      console.error('Error uploading file:', error);

      // Enhanced error handling for different types of upload failures
      let errorMessage = 'An error occurred while uploading the file';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.status === 413) {
          errorMessage = 'File is too large. Server rejected the upload.';
        } else if (axiosError.response?.status === 408 || axiosError.code === 'ECONNABORTED') {
          errorMessage = 'Upload timeout. Please try again with a smaller file or check your connection.';
        } else if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      }

      setUploadError(errorMessage);
      toast.error('Failed to upload file: ' + errorMessage);
    } finally {
      setIsUploading(false);
      setShowLoadingOverlay(false);
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  const handleSubmitDocuments = async () => {
    try {
      setShowLoadingOverlay(true); // Show loading overlay
      await submitDocumentsMutation.mutateAsync({ loanApplicationId: id || '' });
      setIsSubmitted(true);
      toast.success('Documents submitted successfully');
    } catch (error) {
      console.error('Error submitting documents:', error);
      const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
      toast.error(typeof message === 'string' && message.trim() ? message : 'Failed to submit documents');
    } finally {
      setShowLoadingOverlay(false); // Hide loading overlay
    }
  };

  const handleCancelApplication = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    if (!id) {
      toast.error('No application ID available');
      return;
    }
    setIsCancelling(true);
    try {
      await cancelLoanApplication(id, { reason: cancelReason.trim() });
      toast.success('Application cancelled successfully');
      setShowCancelModal(false);
      setCancelReason('');
      navigate('/applications');
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to cancel application';
      toast.error(message);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDownloadDocument = async (documentId: string, documentName: string) => {
    try {
      // Add document to downloading set
      setDownloadingDocuments(prev => new Set(prev).add(documentId));

      // Download the document
      const blob = await downloadDocument(documentId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentName;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Document downloaded successfully');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    } finally {
      // Remove document from downloading set
      setDownloadingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  const formatDocument = (doc: DocumentReference): DisplayDocument => {
    return {
      id: doc.id || '',
      documentType: doc.documentType,
      documentKey: doc.documentKey || '',
      name: doc.name || 'Unknown',
      size: doc.size + ' B',
      uploadDate: getDocumentUploadDate(doc),
      uploadedBy: getDocumentUploader(doc),
      directorId: doc.directorId,
      directorName: doc.directorName,
      period: doc.period,
      documentDate: doc.documentDate,
      expiryDate: doc.expiryDate,
      supersededDate: doc.supersededDate
    };
  };

  /** The current document of a type (for one director, where it is kept per director). */
  const getDocumentByType = (type: string, directorId?: string) => {
    const doc = documents.find(d => d.documentType === type && sameSubject(d.directorId, directorId));
    return doc ? formatDocument(doc) : null;
  };

  const getDocumentsOfType = (type: string, directorId?: string) =>
    documents
      .filter(d => d.documentType === type && sameSubject(d.directorId, directorId))
      .map(formatDocument)
      .sort((a, b) => (b.period || '').localeCompare(a.period || ''));

  const getPreviousVersions = (type: string, directorId?: string) =>
    previousVersions.filter(d => d.documentType === type && sameSubject(d.directorId, directorId));

  const hasDocument = (doc: ExpectedDocument) =>
    doc.multiple
      ? getDocumentsOfType(doc.type, doc.directorId).length > 0
      : getDocumentByType(doc.type, doc.directorId) !== null;

  // The month an officer is about to add a statement for, per multi-file card.
  const [pendingPeriods, setPendingPeriods] = useState<Record<string, string>>({});

  const uploadOptions = (doc: ExpectedDocument, period?: string) => ({
    directorId: doc.directorId,
    directorName: doc.directorName,
    period,
    documentDate: doc.type === 'PAYSLIP' ? payslipDate || undefined : pendingDates[expectedKey(doc)]?.documentDate || undefined,
    expiryDate: pendingDates[expectedKey(doc)]?.expiryDate || undefined
  });

  const renderDateInputs = (doc: ExpectedDocument, inputId: string) => {
    if (doc.type === 'BANK_STATEMENT') return null;

    const key = expectedKey(doc);
    const isPayslip = doc.type === 'PAYSLIP';
    return <div className="mb-2 w-100">
      <div className="d-flex flex-wrap align-items-end gap-2">
        <label htmlFor={`${inputId}-date`} className="small mb-0">
          {isPayslip ? 'Payslip date' : 'Document date (optional)'}
          <input id={`${inputId}-date`} type="date" className="form-control form-control-sm" style={{ maxWidth: 180 }}
            aria-label={isPayslip ? 'Payslip date' : `${doc.label} document date (optional)`}
            value={isPayslip ? payslipDate : pendingDates[key]?.documentDate || ''} disabled={isUploading}
            onChange={e => isPayslip ? setPayslipDate(e.target.value)
              : setPendingDates(prev => ({ ...prev, [key]: { ...prev[key], documentDate: e.target.value } }))} />
        </label>
        <label htmlFor={`${inputId}-expiry`} className="small mb-0">
          Recorded expiry (optional)
          <input id={`${inputId}-expiry`} type="date" className="form-control form-control-sm" style={{ maxWidth: 180 }}
            aria-label={`${doc.label} recorded expiry (optional)`}
            value={pendingDates[key]?.expiryDate || ''} disabled={isUploading}
            onChange={e => setPendingDates(prev => ({ ...prev, [key]: { ...prev[key], expiryDate: e.target.value } }))} />
        </label>
      </div>
      <small className="text-muted d-block mt-1">Use dates printed on the document. Leave an unknown expiry blank.
        These dates apply to the next file you add; they do not change files already uploaded.</small>
    </div>;
  };

  const renderDocumentDates = (document: { documentDate?: string; expiryDate?: string; documentType?: string }) => (
    <>
      <span className="d-block">
        {document.documentType === 'PAYSLIP' ? 'Payslip date' : 'Document date'}: {document.documentDate ? formatDocumentDate(document.documentDate) : 'Not recorded'}
      </span>
      <span className="d-block">Recorded expiry: {document.expiryDate ? formatDocumentDate(document.expiryDate) : 'Not recorded'}</span>
    </>
  );

  const renderUploadMeta = (uploaded: DisplayDocument) => (
    <div className="small text-muted mt-1">
      {renderDocumentDates(uploaded)}
      <span className="d-block">
        Uploaded {formatDocumentDate(uploaded.uploadDate)}
        {uploaded.uploadedBy && <> by {uploaded.uploadedBy}</>}
      </span>
    </div>
  );

  const renderMultiUploadCard = (doc: ExpectedDocument) => {
    const cardKey = expectedKey(doc);
    const files = getDocumentsOfType(doc.type, doc.directorId);
    const inputId = `file-${cardKey.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    const period = pendingPeriods[cardKey] || '';
    const periodMissing = doc.withPeriod && !period;
    // Browsers without a native month picker can supply only the month number.
    const uploadPeriod = /^(0?[1-9]|1[0-2])$/.test(period.trim())
      ? `${new Date().getFullYear()}-${period.trim().padStart(2, '0')}` : period;

    return (
      <div className="col-12" key={cardKey}>
        <div className="document-upload-card flex-column align-items-stretch" role="group" aria-label={doc.label}>
          <div className="document-info">
            <h6 className="document-title">
              {doc.label} {doc.required && <span className="text-danger">*</span>}
              {files.length > 0 && <span className="badge bg-secondary rounded-pill ms-2">{files.length}</span>}
            </h6>
            <p className="document-description">{doc.description}</p>
          </div>

          {files.length > 0 && (
            <div className="table-responsive mb-2">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    {doc.withPeriod && <th>Period</th>}
                    <th>File</th>
                    <th>Document dates</th>
                    <th>Upload date</th>
                    <th>Uploaded by</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {files.map(file => (
                    <tr key={file.id || file.documentKey}>
                      {doc.withPeriod && <td>{formatDocumentPeriod(file.period) || <span className="text-muted">Not set</span>}</td>}
                      <td className="text-break">{file.name}</td>
                      <td className="small text-muted">{renderDocumentDates(file)}</td>
                      <td>
                        {formatDocumentDate(file.uploadDate)}
                      </td>
                      <td>{file.uploadedBy || <span className="text-muted">Unknown</span>}</td>
                      <td className="text-end">
                        <DocumentActionsMenu
                          id={file.id || file.documentKey}
                          actions={[
                            {
                              label: 'View',
                              icon: 'fa-eye',
                              busy: documentPreview.openingKey === file.documentKey,
                              disabled: !file.documentKey,
                              onClick: () => documentPreview.open(file.documentKey, file.name)
                            },
                            {
                              label: 'Download',
                              icon: 'fa-download',
                              busy: downloadingDocuments.has(file.documentKey),
                              disabled: !file.documentKey,
                              onClick: () => handleDownloadDocument(file.documentKey, file.name)
                            },
                            {
                              label: 'Remove',
                              icon: 'fa-trash',
                              destructive: true,
                              busy: removingDocumentId === file.id,
                              disabled: !file.id,
                              onClick: () => handleRemoveDocument(file.id, file.name)
                            }
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {renderDateInputs(doc, inputId)}
          <div className="d-flex flex-wrap align-items-center gap-2">
            {doc.withPeriod && (
              <input
                type="month"
                className="form-control form-control-sm"
                style={{ maxWidth: 180 }}
                value={period}
                aria-label={`${doc.label} period`}
                onChange={e => setPendingPeriods(prev => ({ ...prev, [cardKey]: e.target.value }))}
              />
            )}
            <input
              type="file"
              id={inputId}
              className="d-none"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={async (e) => {
                await handleFileChange(e, doc.type, uploadOptions(doc, uploadPeriod || undefined));
                setPendingPeriods(prev => ({ ...prev, [cardKey]: '' }));
              }}
              disabled={isUploading || periodMissing}
            />
            <label
              htmlFor={inputId}
              className={`btn btn-sm btn-outline-primary mb-0 ${periodMissing ? 'disabled' : ''}`}
              title={periodMissing ? 'Choose the month this statement covers first' : undefined}
            >
              <i className="fa fa-plus me-1"></i>
              Add {doc.withPeriod ? 'Bank Statement' : 'File'}
            </label>
            {periodMissing && <small className="text-muted">Choose the month first</small>}
          </div>
        </div>
      </div>
    );
  };

  const renderUploadCard = (doc: ExpectedDocument) => {
    if (doc.multiple) return renderMultiUploadCard(doc);

    const cardKey = expectedKey(doc);
    const uploaded = getDocumentByType(doc.type, doc.directorId);
    const history = getPreviousVersions(doc.type, doc.directorId);
    const inputId = `file-${cardKey.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    const isPayslip = doc.type === 'PAYSLIP';
    const dateMissing = isPayslip && !payslipDate;

    return (
      <div className="col-md-6" key={cardKey}>
        <div className="document-upload-card flex-column align-items-stretch" role="group" aria-label={doc.label}>
          <div className="document-info me-0 mb-2">
            <h6 className="document-title">
              {doc.label} {doc.required && <span className="text-danger">*</span>}
            </h6>
            <p className="document-description">{doc.description}</p>
            {uploaded ? (
                <>
                  <div className="document-meta">
                    <i className="fa fa-check-circle text-success me-2"></i>
                    <span>{uploaded.name}</span>
                    <span className="bullet">•</span>
                    <span>{uploaded.size}</span>
                  </div>
                  {renderUploadMeta(uploaded)}
                  {history.length > 0 && (
                    <div className="small text-muted">
                      <i className="fa fa-history me-1"></i>
                      Replaced {history.length} time{history.length === 1 ? '' : 's'}; first supplied{' '}
                      {formatDocumentDate(getDocumentUploadDate(history[history.length - 1]))}
                    </div>
                  )}
                </>
            ) : doc.required ? (
                <p className="text-danger mb-0"><small>This document is required</small></p>
            ) : null}
          </div>
          <div className="document-upload flex-column align-items-start w-100">
            {renderDateInputs(doc, inputId)}
            <input
                type="file"
                id={inputId}
                className="d-none"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange(e, doc.type, uploadOptions(doc))}
                disabled={isUploading || dateMissing}
            />
            <label htmlFor={inputId} className={`btn btn-sm mb-0 ${uploaded ? 'btn-success' : 'btn-outline-primary'}${dateMissing ? ' disabled' : ''}`}>
              <i className={`fa ${uploaded ? 'fa-check' : 'fa-upload'} me-2`}></i>
              {uploaded ? 'Replace File' : 'Upload File'}
            </label>
            {dateMissing && <small className="text-muted">Enter the payslip date first</small>}
          </div>
        </div>
      </div>
    );
  };

  // Add function to check if all required documents are uploaded
  const areAllRequiredDocumentsUploaded = () => {
    const requiredDocumentTypes = ['PROOF_OF_ADDRESS', 'BANK_LETTER'];
    return requiredDocumentTypes.every(type => getDocumentByType(type) !== null);
  };

  // KYC status checking functions
  const getKycStatus = () => {
    if (isIndividual) {
      if (individualKyc.isError) return { status: 'missing', message: 'Unable to load individual KYC status', daysAgo: null };
      return individualKycStatus(individualKyc.data?.kycLastUpdated);
    }
    const kycLastUpdated = loanApplication?.company?.kycLastUpdated;

    if (!kycLastUpdated) {
      return { status: 'missing', message: 'KYC has never been performed', daysAgo: null };
    }

    const kycDate = new Date(kycLastUpdated);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - kycDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const threeMonthsInDays = 90;

    if (diffDays > threeMonthsInDays) {
      return { status: 'outdated', message: `KYC was last updated ${diffDays} days ago`, daysAgo: diffDays };
    }

    return { status: 'current', message: `KYC was last updated ${diffDays} days ago`, daysAgo: diffDays };
  };

  const isKycValid = () => {
    const kycStatus = getKycStatus();
    return kycStatus.status === 'current';
  };

  // Handle KYC update
  const handleUpdateKyc = async () => {
    console.log('PARTY: ', partyData);
    if (!partyData?.id) {
      toast.error('Unable to find customer information');
      return;
    }

    try {
      setIsUpdatingKyc(true);
      setKycError(null);
      setShowLoadingOverlay(true);

      if (isIndividual) {
        await confirmIndividualKyc(partyData.id);
        await individualKyc.refetch();
      } else {
      await updateKycMutation.mutateAsync({
        customerId: partyData.id,
        data: {
          kycLastUpdated: new Date().toISOString()
        }
      });
      }

      toast.success('KYC timestamp updated successfully');

      // Refresh the loan application data to get updated KYC info
      await refetchLoanApplication();

    } catch (error) {
      console.error('Error updating KYC:', error);
      let errorMessage = 'Failed to update KYC timestamp';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      }

      setKycError(errorMessage);
      toast.error('Failed to update KYC: ' + errorMessage);
    } finally {
      setIsUpdatingKyc(false);
      setShowLoadingOverlay(false);
    }
  };

  // Simple columns configuration following Documents.tsx pattern
  const columns = [
    {
      name: 'Document Type',
      selector: (row: any) => row.documentType,
      sortable: true,
      cell: (row: any) => (
        <div>
          <div className="fw-bold">{row.documentType.replace(/_/g, ' ').toLowerCase()}</div>
          {(row.directorName || row.period) && (
            <small className="text-muted">
              {[row.directorName, formatDocumentPeriod(row.period)].filter(Boolean).join(' · ')}
            </small>
          )}
        </div>
      ),
    },
    {
      name: 'File Name',
      selector: (row: any) => row.name,
      sortable: true,
    },
    {
      name: 'File Size',
      selector: (row: any) => row.size,
      sortable: true,
    },
    {
      name: 'Upload Date',
      selector: (row: any) => row.uploadDate,
      sortable: true,
      format: (row: any) => formatDocumentDate(row.uploadDate),
    },
    {
      name: 'Uploaded By',
      selector: (row: any) => row.uploadedBy || '',
      sortable: true,
      format: (row: any) => row.uploadedBy || 'Unknown',
    },
    {
      name: 'Document dates',
      selector: (row: any) => row.documentDate || '',
      sortable: true,
      cell: (row: any) => <div className="small text-muted">{renderDocumentDates(row)}</div>,
    },
    {
      name: 'Actions',
      cell: (row: any) => (
        <DocumentActionsMenu
          id={row.id || row.documentKey}
          actions={[
            {
              label: 'View',
              icon: 'fa-eye',
              busy: documentPreview.openingKey === row.documentKey,
              disabled: !row.documentKey,
              onClick: () => documentPreview.open(row.documentKey, row.name)
            },
            {
              label: 'Download',
              icon: 'fa-download',
              busy: downloadingDocuments.has(row.documentKey),
              disabled: !row.documentKey,
              onClick: () => handleDownloadDocument(row.documentKey, row.name)
            },
            {
              label: 'Remove',
              icon: 'fa-trash',
              destructive: true,
              busy: removingDocumentId === row.id,
              disabled: !row.id,
              onClick: () => handleRemoveDocument(row.id, row.name)
            }
          ]}
        />
      ),
    },
  ];

  // Transform documents data for DataTable
  const documentsData = documents.map(formatDocument);

  /**
   * Existing upload-age reminders remain advisory. They are separate from the recorded
   * document/expiry dates and do not establish that the evidence is valid for approval.
   */
  const expiredDocuments = documentsData.filter(doc => isDocumentExpired(doc.uploadDate));

  /** Expected documents this application does not yet have. Advisory, never blocking. */
  const outstandingDocuments = expectedDocuments.filter(doc => !hasDocument(doc));
  const outstandingRequired = outstandingDocuments.filter(doc => doc.required);

  const handleSubmitClick = () => {
    if (outstandingDocuments.length > 0 || expiredDocuments.length > 0) {
      setShowExpiredDocsModal(true);
      return;
    }
    handleSubmitDocuments();
  };

  const handleConfirmSubmitWithExpired = () => {
    setShowExpiredDocsModal(false);
    handleSubmitDocuments();
  };

  /**
   * Existing documents worth offering: those this application does not already have -- by type (and
   * director) for single documents, by file for types that hold several. Recorded document and
   * expiry dates remain visible for review; upload time does not establish currentness.
   */
  const reusableDocuments = (customerDocuments || []).filter(doc =>
    MULTI_FILE_TYPES.has(doc.documentType)
      ? !documents.some(existing => existing.documentType === doc.documentType && existing.name === doc.name
          && sameSubject(existing.period, doc.period))
      : !documents.some(existing => existing.documentType === doc.documentType
          && sameSubject(existing.directorId, doc.directorId))
  );

  const handleReuseDocument = async (documentId: string) => {
    setReusingDocumentId(documentId);
    try {
      await reuseDocumentMutation.mutateAsync({ loanApplicationId: id || '', documentId });
      await Promise.all([refetchDocuments(), refetchCustomerDocuments()]);
      toast.success('Existing document attached to this application');
    } catch (error) {
      console.error('Error reusing document:', error);
      toast.error('Could not attach the existing document');
    } finally {
      setReusingDocumentId(null);
    }
  };

  // Display submission success state
  if (isSubmitted) {
    return (
        <div className="container-fluid p-4">
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <i className="fa fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
              </div>
              <h2 className="mb-3">Documents Submitted Successfully</h2>
              <p className="text-muted mb-4">The loan application documents have been submitted successfully.</p>
              <button
                  className="btn btn-primary mt-3"
                  onClick={() => navigate('/work-items')}
              >
                <i className="fa fa-arrow-left me-2"></i>
                Return to Work Items
              </button>
            </div>
          </div>
        </div>
    );
  }

  if (isLoadingLoan || isLoadingDocuments || isLoadingParty) {
    return (
        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12 text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading document details...</p>
                <small className="text-muted">
                  {isLoadingLoan ? 'Fetching loan application details...' : 'Loading documents...'}
                </small>
              </div>
            </div>
          </div>
        </div>
    );
  }

  const loanDetails = loanApplication?.loanDetails;
  const company = loanApplication?.company;

  return (
      <div className="loan-application-documents">
        {id && <StatementIqPanel applicationId={id} />}
        {/* Add loading overlay */}
        {showLoadingOverlay && (
            <div className="loading-overlay">
              <div className="loading-content">
                <div className="loading-spinner"></div>
                <div className="loading-text">Processing your request</div>
                <div className="loading-subtext">
                  {isUploading ? 'Uploading document... This may take a few minutes for large files.' : 'Please wait while we handle your document'}
                </div>
              </div>
            </div>
        )}

        <div className="page-toolbar">
          <div className="container-fluid">
            <div className="row g-3 align-items-center">
              <div className="col">
                <ol className="breadcrumb bg-transparent mb-0">
                  <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                  <li className="breadcrumb-item"><Link to="/applications">Applications</Link></li>
                  <li className="breadcrumb-item active" aria-current="page">Document Upload</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div className="page-body">
          <div className="container-fluid">
            {/* Modern Loan Details Card */}
            <div className="loan-details-card">
              <div className="loan-details-header">
                <div className="loan-id">
                  <span className="label">Application</span>
                  <h2>{loanApplication?.applicationNumber}</h2>
                </div>
                <div className="loan-status">
                  <span className="badge bg-warning">{loanApplication?.status}</span>
                </div>
              </div>
              <div className="loan-details-body">
                <div className="loan-details-grid">
                  <div className="loan-detail-item">
                    <span className="detail-label">Customer</span>
                    <span className="detail-value">{isIndividual ? loanApplication?.individual?.fullNames || 'N/A' : company?.companyName || 'N/A'}</span>
                  </div>
                  <div className="loan-detail-item highlight">
                    <span className="detail-label">Loan Amount</span>
                    <span className="detail-value">R {loanDetails?.loanAmount?.toLocaleString() || 'N/A'}</span>
                  </div>
                  <div className="loan-detail-item">
                    <span className="detail-label">Term</span>
                    <span className="detail-value">{loanDetails?.numberOfInstallments === 1 ? `${loanDetails?.loanTerms} days` : `${loanDetails?.numberOfInstallments} months`}</span>
                  </div>
                  <div className="loan-detail-item">
                    <span className="detail-label">Application Date</span>
                    <span className="detail-value">{loanApplication?.createdDate ? new Date(loanApplication.createdDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    }) : 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* KYC Status Alert */}
            {(() => {
              const kycStatus = getKycStatus();
              const isKycOutdated = kycStatus.status !== 'current';

              if (isKycOutdated) {
                return (
                  <div className="card mb-4">
                    <div className="card-body">
                      <div className="alert alert-warning mb-0">
                        <div className="d-flex align-items-start">
                          <div className="me-3">
                            <i className="fa fa-exclamation-triangle fa-2x text-warning"></i>
                          </div>
                          <div className="flex-grow-1">
                            <h5 className="alert-heading mb-2">
                              <strong>KYC Update Required</strong>
                            </h5>
                            <p className="mb-3">
                              {kycStatus.message}. Customer KYC verification must be updated before proceeding with document submission.
                            </p>
                            <div className="d-flex gap-3 align-items-center">
                              <button
                                className="btn btn-warning"
                                onClick={handleUpdateKyc}
                                disabled={isUpdatingKyc || !partyData?.id}
                              >
                                {isUpdatingKyc ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin me-2"></i>
                                    Updating KYC...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa fa-refresh me-2"></i>
                                    Mark KYC as Updated
                                  </>
                                )}
                              </button>
                              {!partyData?.id && (
                                <small className="text-muted">
                                  <i className="fa fa-info-circle me-1"></i>
                                  Unable to find customer record
                                </small>
                              )}
                            </div>
                            {kycError && (
                              <div className="mt-3">
                                <div className="alert alert-danger alert-sm mb-0">
                                  <small>{kycError}</small>
                                  <button
                                    type="button"
                                    className="btn-close btn-close-sm"
                                    onClick={() => setKycError(null)}
                                    aria-label="Close"
                                  ></button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="card mb-4">
                    <div className="card-body">
                      <div className="alert alert-success mb-0">
                        <div className="d-flex align-items-center">
                          <div className="me-3">
                            <i className="fa fa-check-circle fa-lg text-success"></i>
                          </div>
                          <div>
                            <strong>KYC Status: Current</strong>
                            <p className="mb-0 mt-1">
                              <small className="text-muted">{kycStatus.message}</small>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            })()}

            {/* Document Upload Card */}
            <div className="card">
              <div className="card-header">
                <h6 className="card-title">
                  <i className="fa fa-file-upload text-primary me-2"></i>
                  Document Upload
                </h6>
              </div>
              <div className="card-body">
                <p className="text-muted mb-4">
                  {isIndividual
                    ? "Upload the applicant's supporting documents. Ensure all documents are clear and legible."
                    : "Upload the client's required business documents. Ensure all documents are clear, legible, and properly verified."}
                </p>

                <div className="alert alert-info mb-4">
                  <div className="d-flex">
                    <div className="me-3">
                      <i className="fa fa-info-circle fa-lg"></i>
                    </div>
                    <div>
                      <p className="mb-0">
                        <strong>System requirements:</strong> PDF, JPG, PNG formats only. Maximum file size: 100MB per document.
                      </p>
                    </div>
                  </div>
                </div>

                {uploadError && (
                    <div className="alert alert-danger mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <span>{uploadError}</span>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setUploadError(null)}
                            aria-label="Close"
                        ></button>
                      </div>
                    </div>
                )}

                <div className="document-section mb-4">
                  <h4 className="section-title mb-3">{isIndividual ? 'Applicant Documents' : 'Company Documents'}</h4>
                  <p className="text-muted mb-3">{isIndividual
                    ? 'Documents required for the individual applicant. Documents marked with * are mandatory.'
                    : <>The company's statutory, banking and financial documents. These stay on the customer's file and can be reused on later applications while still current. Documents marked with <span className="text-danger">*</span> are mandatory.</>}</p>
                  <div className="row g-4">
                    {applicationPartyDocuments.map(renderUploadCard)}
                  </div>
                </div>

                {!isIndividual && (
                  <div className="document-section mb-4">
                    <h4 className="section-title mb-3">Director Documents</h4>
                    <p className="text-muted mb-3">Documents kept for each director on the company profile. They stay on the customer's file and can be reused while still current.</p>
                    {directors.length === 0 ? (
                      <div className="alert alert-light border mb-0">
                        <i className="fa fa-info-circle me-2"></i>
                        No directors are recorded on this company. Add them to the customer profile to collect their documents here.
                      </div>
                    ) : (
                      directors.map(director => (
                        <div key={director.key} className="mb-4">
                          <h6 className="mb-3">
                            <i className="fa fa-user me-2 text-muted"></i>
                            {director.name}
                            {director.idNumber && <small className="text-muted ms-2">ID {director.idNumber}</small>}
                          </h6>
                          <div className="row g-4">
                            {directorDocuments(director).map(renderUploadCard)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {!isIndividual && (
                  <div className="document-section mb-4">
                    <h4 className="section-title mb-3">Company Representative Documents</h4>
                    <p className="text-muted mb-3">Documents for the person submitting or managing the application, who may not be a director.</p>
                    <div className="row g-4">
                      {REPRESENTATIVE_DOCUMENTS.map(renderUploadCard)}
                    </div>
                  </div>
                )}

                {transactionDocuments.length > 0 && <div className="document-section mb-4">
                  <h4 className="section-title mb-3">{fundingProduct ? `${fundingProduct.shortName} Documents` : 'Loan Application Specific Documents'}</h4>
                  <p className="text-muted mb-3">{fundingProduct
                    ? `Transaction-specific documents for the ${fundingProduct.shortName} product chosen on this application. They belong to this application only and are collected again for every new request.`
                    : 'Additional documents required for this specific loan application.'}</p>
                  <div className="row g-4">
                    {transactionDocuments.map(renderUploadCard)}
                  </div>
                </div>}

                {reusableDocuments.length > 0 && (
                  <div className="document-section">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="section-subtitle mb-0">Documents already on file</h6>
                      <span className="badge bg-secondary rounded-pill">{reusableDocuments.length}</span>
                    </div>
                    <p className="text-muted small">
                      This customer supplied these on an earlier application. Attach one instead of
                      asking for it again -- check the validity before you do.
                    </p>
                    <div className="list-group">
                      {reusableDocuments.map(doc => (
                        <div
                          key={doc.id}
                          className="list-group-item d-flex justify-content-between align-items-center flex-wrap gap-2"
                        >
                          <div>
                            <div className="fw-bold">{doc.name || 'Unnamed document'}</div>
                            <small className="text-muted">
                              {String(doc.documentType || '').replace(/_/g, ' ')}
                              {doc.directorName ? ` · ${doc.directorName}` : ''}
                              {doc.period ? ` · ${formatDocumentPeriod(doc.period)}` : ''}
                            </small>
                            <div className="mt-1 small text-muted">
                              {renderDocumentDates(doc)}
                              Uploaded {formatDocumentDate(getDocumentUploadDate(doc))}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleReuseDocument(doc.id || '')}
                            disabled={reusingDocumentId === doc.id}
                          >
                            {reusingDocumentId === doc.id ? (
                              <>
                                <i className="fa fa-spinner fa-spin me-1"></i>
                                Attaching...
                              </>
                            ) : (
                              <>
                                <i className="fa fa-link me-1"></i>
                                Use existing
                              </>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="document-section">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 className="section-subtitle mb-0">Uploaded Documents</h6>
                    <div className="d-flex gap-2">
                      <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => setShowCancelModal(true)}
                      >
                        <i className="fa fa-times-circle me-2"></i>
                        Cancel Application
                      </button>
                      <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSubmitClick}
                          disabled={!isKycValid() || submitDocumentsMutation.status === 'pending'}
                      >
                        {submitDocumentsMutation.status === 'pending' ? (
                            <>
                              <i className="fa fa-spinner fa-spin me-2"></i>
                              Submitting...
                            </>
                        ) : (
                            <>
                              <i className="fa fa-check me-2"></i>
                              Submit Documents
                            </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Submit button help text */}
                  {!isKycValid() && (
                    <div className="mb-3">
                      <small className="text-muted">
                        <i className="fa fa-info-circle me-1"></i>
                        KYC must be updated before document submission
                      </small>
                    </div>
                  )}

                  {outstandingDocuments.length > 0 && (
                    <div className="alert alert-info d-flex align-items-start" role="alert">
                      <i className="fa fa-clipboard-list me-3 mt-1"></i>
                      <div className="flex-grow-1">
                        <strong>
                          {outstandingDocuments.length === 1
                            ? '1 document is still outstanding.'
                            : `${outstandingDocuments.length} documents are still outstanding.`}
                        </strong>
                        <div className="small mt-1">
                          You can submit without them so verification and credit can start; the
                          list stays here and on the application so they can be collected later.
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {outstandingDocuments.map(doc => (
                            <span
                              key={expectedKey(doc)}
                              className={`badge rounded-pill ${doc.required ? 'bg-warning-subtle text-warning-emphasis' : 'bg-light text-dark border'}`}
                            >
                              {doc.label}{doc.directorName ? ` (${doc.directorName})` : ''}{doc.required ? ' (required)' : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {expiredDocuments.length > 0 && (
                    <div className="alert alert-warning d-flex align-items-start" role="alert">
                      <i className="fa fa-exclamation-triangle me-3 mt-1"></i>
                      <div>
                        <strong>
                          {expiredDocuments.length === 1
                            ? '1 document was uploaded more than'
                            : `${expiredDocuments.length} documents were uploaded more than`}
                          {` ${DOCUMENT_VALIDITY_MONTHS} months ago.`}
                        </strong>
                        <div className="small mt-1">
                          Check each document's date and expiry. Ask for a current copy where needed:
                          {' '}{expiredDocuments.map(doc => doc.name).join(', ')}.
                        </div>
                      </div>
                    </div>
                  )}

                  {!documents?.length ? (
                      <div className="alert alert-warning">
                        <i className="fa fa-exclamation-triangle me-2"></i>
                        No documents uploaded yet.
                      </div>
                  ) : (
                      <div className="table-responsive">
                        <DataTable
                            columns={columns}
                            data={documentsData}
                            pagination
                            highlightOnHover
                            striped
                            responsive
                            noDataComponent={
                              <div className="text-center py-4">
                                <i className="fa fa-folder text-muted" style={{ fontSize: '2rem' }}></i>
                                <p className="mt-2">No documents found</p>
                              </div>
                            }
                        />
                      </div>
                  )}

                  {previousVersions.length > 0 && (
                    <details className="mt-4">
                      <summary className="fw-semibold">
                        <i className="fa fa-history me-2"></i>
                        Previous versions ({previousVersions.length})
                      </summary>
                      <p className="text-muted small mt-2">
                        Documents replaced by a newer upload. They are kept on record rather than deleted.
                      </p>
                      <div className="table-responsive">
                        <table className="table table-sm align-middle">
                          <thead>
                            <tr>
                              <th>Document</th>
                              <th>File</th>
                              <th>Uploaded</th>
                              <th>Uploaded by</th>
                              <th>Replaced</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {previousVersions.map(formatDocument).map(version => (
                              <tr key={version.id || version.documentKey}>
                                <td>
                                  {version.documentType.replace(/_/g, ' ').toLowerCase()}
                                  {version.directorName && <div className="small text-muted">{version.directorName}</div>}
                                </td>
                                <td className="text-break">{version.name}</td>
                                <td>{formatDocumentDate(version.uploadDate)}</td>
                                <td>{version.uploadedBy || 'Unknown'}</td>
                                <td>{formatDocumentDate(version.supersededDate)}</td>
                                <td className="text-end">
                                  <DocumentActionsMenu
                                    id={version.id || version.documentKey}
                                    actions={[
                                      {
                                        label: 'View',
                                        icon: 'fa-eye',
                                        busy: documentPreview.openingKey === version.documentKey,
                                        disabled: !version.documentKey,
                                        onClick: () => documentPreview.open(version.documentKey, version.name)
                                      },
                                      {
                                        label: 'Download',
                                        icon: 'fa-download',
                                        busy: downloadingDocuments.has(version.documentKey),
                                        disabled: !version.documentKey,
                                        onClick: () => handleDownloadDocument(version.documentKey, version.name)
                                      }
                                    ]}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Comments Section */}
          {loanApplication && (
            <LoanApplicationComments
              loanApplicationId={loanApplication.id!}
              currentStage={loanApplication.status || 'DRAFT'}
              className="mt-4"
            />
          )}

          <DocumentPreviewModal preview={documentPreview.preview} onClose={documentPreview.close} />

          {/* Expired documents confirmation ---------------------------------------------- */}
          {showExpiredDocsModal && (
            <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fa fa-exclamation-triangle text-warning me-2"></i>
                      Submit with gaps in the file?
                    </h5>
                    <button type="button" className="btn-close" onClick={() => setShowExpiredDocsModal(false)}></button>
                  </div>
                  <div className="modal-body">
                    {outstandingDocuments.length > 0 && (
                      <>
                        <p className="mb-2">Still outstanding on this application:</p>
                        <ul className="mb-3">
                          {outstandingDocuments.map(doc => (
                            <li key={expectedKey(doc)}>
                              {doc.label}{doc.directorName ? ` (${doc.directorName})` : ''}
                              {doc.required && <span className="text-warning-emphasis"> (required)</span>}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    {expiredDocuments.length > 0 && (
                      <>
                        <p className="mb-2">
                          {expiredDocuments.length === 1
                            ? 'This document was uploaded more than'
                            : 'These documents were uploaded more than'}
                          {` ${DOCUMENT_VALIDITY_MONTHS} months ago and may no longer reflect the client's position:`}
                        </p>
                        <ul className="mb-3">
                          {expiredDocuments.map(doc => (
                            <li key={doc.id}>{doc.name}</li>
                          ))}
                        </ul>
                      </>
                    )}

                    <p className="text-muted small mb-0">
                      {outstandingRequired.length > 0
                        ? 'Submitting lets verification and credit start in parallel. What is outstanding stays recorded against the application and will be asked for before a decision.'
                        : 'Submitting is allowed. Verification and credit may still ask for current copies.'}
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary rounded-pill px-4"
                      onClick={() => setShowExpiredDocsModal(false)}
                    >
                      Go back
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning rounded-pill px-4"
                      onClick={handleConfirmSubmitWithExpired}
                    >
                      Submit anyway
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Cancel Application Modal */}
          {showCancelModal && (
            <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
                  <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8d7da', border: '1px solid rgba(220,53,69,0.3)' }}>
                        <i className="fa fa-times-circle text-danger" style={{ display: 'block' }}></i>
                      </div>
                      <div>
                        <h5 className="modal-title mb-0 fw-bold">Cancel Application</h5>
                        <p className="text-muted mb-0 small">This action cannot be undone</p>
                      </div>
                    </div>
                    <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)} disabled={isCancelling}></button>
                  </div>
                  <div className="modal-body p-4">
                    <p className="text-muted mb-3">Are you sure you want to cancel this loan application? Please provide a reason:</p>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Enter reason for cancellation..."
                      disabled={isCancelling}
                      maxLength={500}
                    />
                    <small className="text-muted">{cancelReason.length}/500 characters</small>
                  </div>
                  <div className="modal-footer border-0 px-4 pb-4">
                    <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
                      Close
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger rounded-pill px-4"
                      onClick={handleCancelApplication}
                      disabled={isCancelling || !cancelReason.trim()}
                    >
                      {isCancelling ? (
                        <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Cancelling...</>
                      ) : (
                        <><i className="fa fa-times-circle me-2"></i>Cancel Application</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
  );
}

export default IndividualLoanApplicationDocuments;
